import { STORAGE_KEYS } from '@/constants';

/** How many completion ids to remember. Old ones fall off the front — a
 * candidate revisiting a results page from months ago getting the prompt
 * once more is harmless; an unbounded list in localStorage isn't. */
export const MAX_PROMPTED_IDS = 50;

type PromptStorage = Pick<Storage, 'getItem' | 'setItem'>;

const defaultStorage = (): PromptStorage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

const readIds = (storage: PromptStorage): string[] => {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(STORAGE_KEYS.NEXT_PART_PROMPTED) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

/** Whether the "what's next" prompt already showed for this finished
 * assessment or HTS submission. Unreadable storage counts as "not yet" —
 * showing it twice beats never showing it. */
export function wasPrompted(completionId: string, storage: PromptStorage | null = defaultStorage()): boolean {
  if (!storage) return false;
  return readIds(storage).includes(completionId);
}

export function markPrompted(completionId: string, storage: PromptStorage | null = defaultStorage()): void {
  if (!storage) return;
  const ids = readIds(storage).filter((id) => id !== completionId);
  ids.push(completionId);
  try {
    storage.setItem(STORAGE_KEYS.NEXT_PART_PROMPTED, JSON.stringify(ids.slice(-MAX_PROMPTED_IDS)));
  } catch {
    /* storage full or blocked — the prompt may show again, which is fine */
  }
}
